/**
 * Pure helpers and constants for the file-import flow (upload → map columns →
 * analyze), shared by the standalone file-import feature and the embedded
 * import steps of the list wizards. No React, no Redux, no fetch.
 */

import {
  ADMIN_ONLY_IMPORT_FIELDS,
  type CsvAnalyzeResponse,
  type ExitValues,
  PROFILE_IMPORT_FIELDS,
  type ProfileImportField,
} from '@shared/schemas';

import { compactAmount, formatExitDateFr } from '@/utils/format';

/** File extensions the import accepts (kept 1:1 with the backend parser). */
export const ACCEPTED_EXTENSIONS = ['.csv', '.xlsx', '.xls', '.json'];

/** `accept` attribute value for the hidden file input. */
export const ACCEPTED_MIME = '.csv,.xlsx,.xls,.json';

/** Snackbar copy when a dropped file has an unsupported extension. */
export const INVALID_FILE_MESSAGE = 'Please upload a .csv, .xlsx, or .json file';

/** Friendly labels for profile fields (column-mapping selects and previews). */
export const FIELD_LABELS: Record<string, string> = {
  linkedin_url: 'LinkedIn URL *',
  linkedin_id: 'LinkedIn ID',
  full_name: 'Full Name',
  first_name: 'First Name',
  last_name: 'Last Name',
  headline: 'Headline',
  about: 'About',
  profile_image_url: 'Profile Image URL',
  job_title: 'Job Title',
  company: 'Company',
  company_logo_url: 'Company Logo URL',
  company_industry: 'Company Industry',
  company_employee_range: 'Company Size',
  city: 'City',
  country: 'Country',
  email: 'Email',
  phone: 'Phone',
  exit_company_name: 'Exit Company Name',
  exit_company_linkedin_url: 'Exit Company LinkedIn URL',
  exit_date: 'Exit Date',
  exit_amount: 'Exit Amount',
  exit_currency: 'Exit Currency',
  connection_count: 'Connections',
  follower_count: 'Followers',
  school: 'School',
};

/**
 * The date formats the importer reads for an exit date (kept 1:1 with the
 * backend's parseImportDate), shown once a column is mapped to it (#700).
 */
export const EXIT_DATE_FORMATS_HINT =
  'Exit Date reads 15/03/2024, 2024-03-15, 03/2024, 2024-03, 2024 or an Excel date cell. Day comes first: 04/05/2024 is 4 May 2024.';

/**
 * Label for a field named as its database column: what the analyze step's
 * "fields to update" carries (`fullName`, `exitDate`). Falls back to a
 * humanised form so a column with no import label still reads as words.
 */
export function updatedFieldLabel(field: string): string {
  const snake = field.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();
  const label = FIELD_LABELS[snake] ?? FIELD_LABELS[field];
  // `linkedin_url` carries the "required" asterisk of the mapping select; the
  // chips are not a form, so it is dropped here.
  if (label) return label.replace(/ \*$/, '');
  return snake
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** Rotating status messages shown while the analyze request is in flight. */
export const ANALYZE_PHASES = [
  'Normalizing URLs…',
  'Validating LinkedIn URLs…',
  'Checking profiles in database…',
  'Detecting duplicates…',
  'Computing statistics…',
];

/** Detect the import source label from a filename. */
export function detectSource(filename: string): 'csv_import' | 'excel_import' | 'json_import' {
  const lower = filename.toLowerCase();
  if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) return 'excel_import';
  if (lower.endsWith('.json')) return 'json_import';
  return 'csv_import';
}

/** Does this filename carry one of the accepted import extensions? */
export function isAcceptedImportFile(filename: string): boolean {
  const lower = filename.toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

/** Default import name derived from a filename (extension stripped). */
export function defaultImportName(fileName: string): string {
  return fileName.replace(/\.[^.]+$/, '');
}

/** The set of profile fields currently mapped (ignoring empty / "ignore" entries). */
export function getMappedFields(columnMapping: Record<string, string>): Set<string> {
  return new Set(Object.values(columnMapping).filter((v) => v && v !== 'ignore'));
}

/** The import requires a linkedin_url mapping before analyze can run. */
export function hasLinkedInUrl(columnMapping: Record<string, string>): boolean {
  return getMappedFields(columnMapping).has('linkedin_url');
}

/** Headers that are mapped to a profile field, in original header order. */
export function getMappedHeaders(
  headers: string[],
  columnMapping: Record<string, string>,
): string[] {
  return headers.filter((h) => columnMapping[h] && columnMapping[h] !== 'ignore');
}

/**
 * The fields a mapping select offers (#709): the exit company columns are an
 * administrator's only; the backend refuses them from anyone else.
 */
export function importFieldsFor(isAdmin: boolean): readonly ProfileImportField[] {
  if (isAdmin) return PROFILE_IMPORT_FIELDS;
  return PROFILE_IMPORT_FIELDS.filter(
    (f) => !(ADMIN_ONLY_IMPORT_FIELDS as readonly string[]).includes(f),
  );
}

type ExitConflict = CsvAnalyzeResponse['exit_conflicts'][number];

/** Why a row asks before it changes an exit on file (#709). */
export const EXIT_CONFLICT_REASON: Record<ExitConflict['reason'], string> = {
  company_values_differ: 'Same company, another year or amount',
  same_year_without_company: 'An exit without company that year, another amount',
  same_year_values_differ: 'An exit that year with other values',
};

/** "Acme · 2021 · €5M": one exit on a line; "-" for what it does not say. */
export function describeExitValues(v: ExitValues): string {
  return [
    v.company_name ?? 'No company',
    v.exit_date ? formatExitDateFr(v.exit_date, v.exit_date_precision) : 'no date',
    v.exit_amount != null ? compactAmount(v.exit_amount, v.exit_currency) : 'no amount',
  ].join(' · ');
}

/**
 * Toggle a conflict's overwrite (#709): the administrator decides row by row,
 * never for the whole file. Kept sorted, so the request reads like the file.
 */
export function toggleOverwriteRow(rows: readonly number[], row: number): number[] {
  return rows.includes(row) ? rows.filter((r) => r !== row) : [...rows, row].sort((a, b) => a - b);
}
